import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut, getIdTokenResult } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db, requestNotificationPermission } from '../firebase.js';
import { ROLE_PERMISSION_PRESETS, PANEL_PERMISSION_DEPENDENCIES } from '../constants/permissionCatalog.js';
import { ALL_PERMISSIONS } from '../constants/permissions.js';
import { SUPER_ADMIN_UIDS } from '../constants/appConfig.js';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// --- NUEVO: Componente para manejar la redirección ---
// Este componente se renderizará DENTRO del Router y podrá usar useNavigate.
export const AuthRedirector = () => {
    const { isAdmin, isSupport } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if ((isAdmin || isSupport) && window.location.pathname.startsWith('/login')) {
            navigate(isAdmin ? '/dashboard' : '/soporte');
        }
    }, [isAdmin, isSupport, navigate]);

    return null; // Este componente no renderiza nada visualmente.
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [platformConfig, setPlatformConfig] = useState(null); // <-- NUEVO: Estado para la configuración
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSupport, setIsSupport] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [userRole, setUserRole] = useState('guest');
    const [loading, setLoading] = useState(true); // Este loading ahora será el único que necesitemos
    const emptyPermissionMap = useMemo(() => {
        const base = {};
        ALL_PERMISSIONS.forEach(permission => {
            base[permission] = false;
        });
        return base;
    }, []);
    const [adminPermissions, setAdminPermissions] = useState(() => ({}));
    
    useEffect(() => {
        let unsubscribeProfile = null;
        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {

            if (user) {
                setCurrentUser(user);
                const superAdmin = SUPER_ADMIN_UIDS.includes(user.uid);
                setIsSuperAdmin(superAdmin);

                try {
                    // 1. Obtener los claims de admin (NO forzamos refresh de token)
                    const idTokenResult = await getIdTokenResult(user);
                    const claims = idTokenResult.claims || {};
                    const claimAdmin = !!claims.admin;
                    const claimSupport = !!claims.support;

                    const effectiveAdmin = superAdmin || claimAdmin;
                    const effectiveSupport = !superAdmin && claimSupport;

                    setIsAdmin(effectiveAdmin);
                    setIsSupport(effectiveSupport);
                    setUserRole(superAdmin ? 'superadmin' : (claimAdmin ? 'admin' : (claimSupport ? 'support' : 'user')));

                    // 2. Cargar el perfil una vez rápido con getDoc para acelerar el primer render
                    const profileRef = doc(db, `users/${user.uid}/profile/data`);
                    try {
                        const docSnap = await getDoc(profileRef);
                        if (docSnap && docSnap.exists()) {
                            setUserProfile(docSnap.data());
                        } else {
                            setUserProfile(null);
                        }
                    } catch (err) {
                        console.error('Error leyendo perfil con getDoc:', err);
                        setUserProfile(null);
                    }

                    // Ya tenemos los datos mínimos para mostrar la UI
                    setLoading(false);

                    // 3. Suscribirse a actualizaciones en background (no bloqueante)
                    unsubscribeProfile = onSnapshot(profileRef, (docSnap) => {
                        if (docSnap.exists()) {
                            setUserProfile(docSnap.data());
                        } else {
                            setUserProfile(null);
                        }
                    }, (error) => {
                        console.error("Error al escuchar el perfil de usuario:", error);
                    });

                    // 4. Request de permiso de notificaciones en background (no await, no bloquea)
                    requestNotificationPermission(user.uid).catch(err => console.error('Error requesting notification permission:', err));

                } catch (error) {
                    console.error("Error obteniendo claims de usuario:", error);
                    setIsAdmin(false);
                    setIsSupport(false);
                    setIsSuperAdmin(false);
                    setUserRole('guest');
                    setLoading(false);
                }
            } else {
                setCurrentUser(null);
                setUserProfile(null);
                setIsAdmin(false);
                setIsSupport(false);
                setIsSuperAdmin(false);
                setUserRole('guest');
                setLoading(false);
            }
        });

        return () => {
            try { unsubscribeAuth(); } catch (e) {}
            try { if (typeof unsubscribeProfile === 'function') unsubscribeProfile(); } catch (e) {}
        };
    }, []);
    
    // --- CORRECCIÓN: Hook para cargar y escuchar la configuración de la plataforma en tiempo real ---
    // Este listener ahora se aplica a todos los usuarios, pero el panel de admin solo se muestra a los admins.
    useEffect(() => {
        const configRef = doc(db, 'platform_config/main');
        // Usamos onSnapshot para que cualquier cambio en la configuración se refleje en toda la app al instante.
        const unsubscribeConfig = onSnapshot(configRef, (docSnap) => {
            if (docSnap.exists()) {
                setPlatformConfig(docSnap.data());
            } else {
                // Si la configuración no existe, podemos establecer un valor por defecto o mostrar una advertencia.
                console.warn("Documento 'platform_config/main' no encontrado.");
                setPlatformConfig({ withdrawalsEnabled: true }); // Fallback seguro
            }
        }, (error) => console.error("Error escuchando la configuración de la plataforma:", error));

        return () => unsubscribeConfig();
    }, []); // Se ejecuta una vez para todos los usuarios.

    const deriveRolePreset = useCallback((roleKey, config) => {
        const basePreset = ROLE_PERMISSION_PRESETS[roleKey] || {};
        const configPreset = config?.rolePermissionPresets?.[roleKey] || {};
        const combined = { ...emptyPermissionMap };
        ALL_PERMISSIONS.forEach(permission => {
            const baseValue = basePreset[permission] ?? false;
            const overrideValue = configPreset[permission];
            combined[permission] = overrideValue !== undefined ? !!overrideValue : !!baseValue;
        });
        return combined;
    }, [emptyPermissionMap]);

    useEffect(() => {
        const roleKey = isSuperAdmin ? 'superadmin' : (userRole || (isAdmin ? 'admin' : (isSupport ? 'support' : 'user')));
        const rolePreset = deriveRolePreset(roleKey, platformConfig);
        const userOverrides = userProfile?.permissions;
        const finalPermissions = { ...rolePreset };

        if (userOverrides && typeof userOverrides === 'object') {
            Object.entries(userOverrides).forEach(([permission, value]) => {
                if (permission in finalPermissions) {
                    finalPermissions[permission] = !!value;
                }
            });
        }

        Object.entries(PANEL_PERMISSION_DEPENDENCIES).forEach(([panelPermission, requiredPermissions]) => {
            if (finalPermissions[panelPermission]) {
                requiredPermissions.forEach((dependency) => {
                    finalPermissions[dependency] = true;
                });
            }
        });

        if (roleKey === 'support') {
            finalPermissions['panel:soporte'] = true;
            finalPermissions['support:handle_tickets'] = true;
        }

        if (isSuperAdmin) {
            ALL_PERMISSIONS.forEach(permission => {
                finalPermissions[permission] = true;
            });
        }

        setAdminPermissions(finalPermissions);
    }, [platformConfig, userProfile, userRole, isAdmin, isSupport, isSuperAdmin, deriveRolePreset]);

    const hasPermission = useCallback((permission) => {
        if (!permission) {
            return true;
        }
        return !!adminPermissions?.[permission];
    }, [adminPermissions]);

    const logout = () => firebaseSignOut(auth);

    const value = {
        currentUser,
        userProfile,
        isAdmin,
        isSupport,
        isSuperAdmin,
        userRole,
        platformConfig, // <-- NUEVO: Exponer la configuración
        setPlatformConfig, // <-- NUEVO: Exponer el setter para actualizaciones optimistas
        adminPermissions,
        hasPermission,
        loading,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};